import React, { useCallback, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Input, Popover, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, HolderOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import { v4 as uuidv4 } from 'uuid'
import { CnDatahubCategoryColumn, DATAHUB_COLUMN_TYPE_OPTIONS, DatahubColumnType } from './datahub-interfaces'
import { DATAHUB_PRIVATE_TITLE_KEY, isPrivateTitleColumn } from './datahubDefaults'
import { parseSelectOptions, serializeSelectOptions } from './datahubFilter'
import { defaultOrderKeyForIndex, reindexOrderKeys, sortDatahubColumns } from './datahubOrderKey'

const OPTIONS_VISIBLE_COUNT = 3

export type DatahubColumnsEditorMode = 'private' | 'shared'

interface DatahubColumnsEditorProps {
  columns: CnDatahubCategoryColumn[]
  onChange: (columns: CnDatahubCategoryColumn[]) => void
  categoryId: string
  categoryAccessId?: string
  mode?: DatahubColumnsEditorMode
  isColumnOverridden?: (column: CnDatahubCategoryColumn) => boolean
  onToggleOverride?: (column: CnDatahubCategoryColumn, checked: boolean) => void
  onReorder?: (fromIndex: number, toIndex: number) => void
}

/**
 * Get the key of a column row
 * @param column - The column
 * @returns The key of the column row
 */
function columnRowKey(column: CnDatahubCategoryColumn): string {
  // If the column has a category column id, use it, otherwise use the id
  return column.categoryColumnId ?? column.id
}

const DragHandle = SortableHandle(() => (
  <HolderOutlined style={{ cursor: 'grab', color: 'rgba(0,0,0,0.45)' }} />
))

/**
 * Sortable body row
 * @param props - The props of the row 
 * @returns The sortable row
 */
const SortableBodyRow = SortableElement(
  (props: React.HTMLAttributes<HTMLTableRowElement>) => <tr {...props} />,
)

/**
 * Sortable table body
 * @param props - The props of the table body
 * @returns The sortable table body
 */
const SortableTableBody = SortableContainer(
  (props: React.HTMLAttributes<HTMLTableSectionElement>) => <tbody {...props} />,
)

/**
 * Slugify a text (ex: "Nom dans l'interface" -> "nom_dans_l_interface")
 * @param text - The text to slugify
 * @returns The slugified text
 */
function slugifyColumnKey(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Generate a unique column key
 * @param textForUniqueKey - The text to use for the unique key (ex: "nom_dans_l_interface")
 * @param columns - The columns to check
 * @param excludeColumnId - The id of the column to exclude
 * @returns The unique column key
 */
function uniqueColumnKey(
  textForUniqueKey: string,
  columns: CnDatahubCategoryColumn[],
  excludeColumnId?: string,
): string {
  const fallback = textForUniqueKey || 'colonne'
  const used = new Set(
    columns
      .filter((col) => col.id !== excludeColumnId)
      .map((col) => col.datakey)
      .filter(Boolean),
  )
  // Reserved system key for the private title column
  used.add(DATAHUB_PRIVATE_TITLE_KEY)
  // If the fallback is not used, return it
  if (!used.has(fallback)) return fallback
  // If the fallback is used, generate a new key
  let index = 2
  while (used.has(`${fallback}_${index}`)) {
    index += 1
  }
  return `${fallback}_${index}`
}

/**
 * Generate a column key from a name
 * @param name - The name of the column
 * @param columns - The columns to check
 * @param excludeColumnId - The id of the column to exclude
 * @returns The column key
 */
function columnKeyFromName(
  name: string,
  columns: CnDatahubCategoryColumn[],
  excludeColumnId?: string,
): string {
  return uniqueColumnKey(slugifyColumnKey(name), columns, excludeColumnId)
}

/**
 * Input for the options of a column type "select" or "multiple-labels".
 * Options are stacked vertically (full text). Beyond OPTIONS_VISIBLE_COUNT, a "voir plus" popover lists them all.
 */
function DatahubColumnOptionsInput({
  value,
  disabled,
  onChange,
}: {
  value: string | null | undefined
  disabled: boolean
  onChange: (options: string | null) => void
}) {
  const tags = parseSelectOptions(value)
  const [inputValue, setInputValue] = useState('')
  const [popoverOpen, setPopoverOpen] = useState(false)

  const commitInput = (raw: string) => {
    const parts = raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
    if (parts.length === 0) return

    const next = [...tags]
    for (const part of parts) {
      if (!next.includes(part)) next.push(part)
    }
    onChange(serializeSelectOptions(next))
    setInputValue('')
  }

  const removeTag = (tag: string) => {
    onChange(serializeSelectOptions(tags.filter((item) => item !== tag)))
  }

  const addInput = !disabled && (
    <Input
      size="small"
      style={{ width: '100%', minWidth: 100 }}
      value={inputValue}
      placeholder="Entrée pour ajouter"
      onChange={(e) => setInputValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commitInput(inputValue)
        }
      }}
      onBlur={() => {
        if (inputValue.trim()) {
          commitInput(inputValue)
        }
      }}
    />
  )

  const renderTagList = (items: string[], closable: boolean) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }}>
      {items.map((tag) => (
        <Tag
          key={tag}
          closable={closable && !disabled}
          onClose={() => removeTag(tag)}
          style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '100%' }}
        >
          {tag}
        </Tag>
      ))}
    </div>
  )

  const visibleTags = tags.slice(0, OPTIONS_VISIBLE_COUNT)
  const hiddenCount = tags.length - OPTIONS_VISIBLE_COUNT
  const hasHidden = hiddenCount > 0

  const popoverContent = (
    <div style={{ width: 260, maxHeight: 260, overflowY: 'auto' }}>
      <div style={{ marginBottom: disabled ? 0 : 8 }}>{renderTagList(tags, true)}</div>
      {addInput}
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
      {renderTagList(visibleTags, !hasHidden)}
      {hasHidden && (
        <Popover
          content={popoverContent}
          title="Options"
          trigger="click"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
        >
          <Tag color="blue" style={{ cursor: 'pointer', margin: 0, alignSelf: 'flex-start' }}>
            +{hiddenCount} voir plus
          </Tag>
        </Popover>
      )}
      {!hasHidden && addInput}
    </div>
  )
}

export function DatahubColumnsEditor({
  columns,
  onChange,
  categoryId,
  categoryAccessId,
  mode = 'private',
  isColumnOverridden,
  onToggleOverride,
  onReorder,
}: DatahubColumnsEditorProps) {
  const isShared = mode === 'shared'

  /**
   * Check if a field is editable
   * @param field - The field to check
   * @param record - The record to check
   * @returns true if the field is editable, false otherwise
   */
  const isFieldEditable = useCallback(
    (field: keyof CnDatahubCategoryColumn, record: CnDatahubCategoryColumn) => {
      // Mandatory title column : only display name and description are editable
      if (isPrivateTitleColumn(record)) {
        return field === 'name' || field === 'description'
      }
      // If the columns are shared, check if the column is overridden
      if (isShared) {
        if (!isColumnOverridden?.(record)) return false
        // If the column is overridden, check if the field is editable
        return field === 'name' || field === 'visible' || field === 'description'
      }
      // If the columns are private, check if the field is editable
      return field !== 'label' && field !== 'datakey' && field !== 'orderKey'
    },
    [isShared, isColumnOverridden],
  )

  /**
   * Update a column
   * @param columnId - The id of the column to update
   * @param patch - The patch to apply to the column
   */
  const updateColumn = useCallback(
    (columnId: string, patch: Partial<CnDatahubCategoryColumn>) => {
      onChange(columns.map((col) => (col.id === columnId ? { ...col, ...patch } : col)))
    },
    [columns, onChange],
  )

  /**
   * Finalize the key of an private column
   * @param columnId - The id of the column to finalize
   * @param name - The name of the column to finalize
   */
  const finalizePrivateColumnKey = useCallback(
    (columnId: string, name: string) => {
      const column = columns.find((col) => col.id === columnId)
      // Check if the column exists + has not been saved into DB yet + has a name
      if (!column || column.categoryColumnId !== null || !name.trim()) return
      // Never rewrite the reserved title identifier
      if (isPrivateTitleColumn(column)) return
      // Generate the new key
      const key = columnKeyFromName(name, columns, columnId)
      onChange(
        columns.map((col) =>
          col.id === columnId ? { ...col, label: key, datakey: key } : col,
        ),
      )
    },
    [columns, onChange],
  )

  /**
   * Delete a column from the list of columns
   * @param columnId - The id of the column to delete
   */
  const deleteColumn = useCallback(
    (columnId: string) => {
      if (isShared) return
      const column = columns.find((col) => col.id === columnId)
      if (!column || isPrivateTitleColumn(column)) return
      const updatedColumns = columns.filter((col) => col.id !== columnId)
      onChange(reindexOrderKeys(updatedColumns))
    },
    [columns, isShared, onChange],
  )

  /**
   * Move a column to a new position (use onReorder function if the columns are shared)
   * @param fromIndex - The index of the column to move
   * @param toIndex - The new index of the column
   */
  const moveColumnTo = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return
      // If the columns are shared, call the onReorder function (override-aware order keys)
      if (isShared && onReorder) {
        onReorder(fromIndex, toIndex)
        return
      }
      // Private: rearrange then reindex all order keys sequentially
      const sorted = sortDatahubColumns(columns)
      const moved = sorted[fromIndex]
      if (!moved || toIndex < 0 || toIndex >= sorted.length) return

      const withoutMoved = sorted.filter((_, index) => index !== fromIndex)
      const reordered = [
        ...withoutMoved.slice(0, toIndex),
        moved,
        ...withoutMoved.slice(toIndex),
      ]
      onChange(reindexOrderKeys(reordered))
    },
    [columns, isShared, onChange, onReorder],
  )

  /**
   * Add a new column to the list of columns
   */
  const addColumn = useCallback(() => {
    if (isShared) return
    const datakey = uniqueColumnKey('nouvelle_colonne', columns)
    const newColumn: CnDatahubCategoryColumn = {
      id: uuidv4(),
      categoryId,
      categoryAccessId: null,
      categoryColumnId: null,
      label: datakey,
      datakey,
      name: '',
      type: 'text',
      required: false,
      visible: true,
      options: null,
      description: null,
      orderKey: defaultOrderKeyForIndex(columns.length),
    }
    onChange([...columns, newColumn])
  }, [categoryId, columns, onChange])

  const sortedColumns = sortDatahubColumns(columns)
  const titleColumn = sortedColumns.find(isPrivateTitleColumn)
  const titleDisplayLabel = titleColumn?.name?.trim() || titleColumn?.label || 'Titre'
  const [isDragging, setIsDragging] = useState(false)

  // Use refs so DraggableContainer/DraggableBodyRow are stable references (never recreated),
  // preventing Ant Design from unmounting the table body on every keystroke.
  const moveColumnToRef = useRef(moveColumnTo)
  moveColumnToRef.current = moveColumnTo
  const setIsDraggingRef = useRef(setIsDragging)
  setIsDraggingRef.current = setIsDragging
  const sortedColumnsRef = useRef(sortedColumns)
  sortedColumnsRef.current = sortedColumns

  /**
   * Draggable container
   * @param props - The props of the container
   * @returns The draggable container
   */
  const DraggableContainer = useRef(
    (props: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <SortableTableBody
        useDragHandle
        disableAutoscroll
        helperClass="datahub-column-row-dragging"
        onSortStart={() => setIsDraggingRef.current(true)}
        onSortEnd={({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
          moveColumnToRef.current(oldIndex, newIndex)
          setIsDraggingRef.current(false)
        }}
        {...props}
      />
    ),
  ).current

  /**
   * Draggable body row
   * @param props - The props of the row
   * @returns The draggable body row
   */
  const DraggableBodyRow = useRef(
    (props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key'?: string }) => {
      const index = sortedColumnsRef.current.findIndex(
        (col) => columnRowKey(col) === props['data-row-key'],
      )
      return <SortableBodyRow index={index >= 0 ? index : 0} {...props} />
    },
  ).current

  /**
   * Get the columns of the table
   * @returns The columns of the table
   */
  const tableColumns = [
    // Column to drag and drop the columns
    {
      title: '',
      key: 'reorder',
      width: 40,
      align: 'center' as const,
      render: () => <DragHandle />,
    },
    ...(isShared
      ? [
        {
          title: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Label admin
            <Tooltip title="Label affiché uniquement dans l'admin pour faire le lien avec la bonne colonne dans la configuration des filtres">
              <InfoCircleOutlined />
            </Tooltip>
          </span>,
          dataIndex: 'label',
          key: 'label',
          width: 140,
          render: (label: string, record: CnDatahubCategoryColumn) => (
            <Input value={label || record.label} disabled placeholder="Label par défaut" />
          ),
        }
      ]
      : [
        {
          title: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Identifiant
            <Tooltip title="Défini à la création de la colonne (non modifié ensuite), utilisé par l'IT pour la sauvegarde des données">
              <InfoCircleOutlined />
            </Tooltip>
          </span>,
          dataIndex: 'datakey',
          key: 'datakey',
          width: 160,
          render: (_: string, record: CnDatahubCategoryColumn) => (
            <Typography.Text code style={{ fontSize: 12 }}>
              {record.datakey || '—'}
            </Typography.Text>
          ),
        },
      ]),
    {
      title: 'Nom affiché',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      render: (_: string, record: CnDatahubCategoryColumn) => (
        <Tooltip title={record.name}>
          <Input
            value={record.name}
            disabled={!isFieldEditable('name', record)}
            placeholder="Nom"
            onChange={(e) => updateColumn(record.id, { name: e.target.value })}
            onBlur={(e) => {
              if (!isShared) {
                finalizePrivateColumnKey(record.id, e.target.value)
              }
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (_: DatahubColumnType, record: CnDatahubCategoryColumn) =>
        // If the columns are being dragged, display the type as a text
        isDragging ? (
          <Typography.Text>
            {DATAHUB_COLUMN_TYPE_OPTIONS.find((option) => option.value === record.type)?.label ??
              record.type}
          </Typography.Text>
        ) : (
          <Select
            value={record.type}
            disabled={!isFieldEditable('type', record)}
            style={{ width: '100%' }}
            options={DATAHUB_COLUMN_TYPE_OPTIONS}
            onChange={(value: DatahubColumnType) =>
              updateColumn(record.id, {
                type: value,
                options: value === 'select' || value === 'multiple-labels' ? record.options : null,
              })
            }
          />
        ),
    },
    {
      title: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        Options
        <Tooltip title="Pour ajouter des options, entrez une valeur, puis appuyez sur Entrée. Au-delà de 3 options, utilisez « voir plus ».">
          <InfoCircleOutlined />
        </Tooltip>
      </span>,
      dataIndex: 'options',
      key: 'options',
      width: 160,
      render: (_: string | null | undefined, record: CnDatahubCategoryColumn) => (
        <DatahubColumnOptionsInput
          value={record.options}
          disabled={!isFieldEditable('options', record) || (record.type !== 'select' && record.type !== 'multiple-labels')}
          onChange={(options) => updateColumn(record.id, { options })}
        />
      ),
    },
    {
      title: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        Obligatoire
        <Tooltip title="Si la colonne est obligatoire, l'utilisateur ne pourra pas la laisser vide">
          <InfoCircleOutlined />
        </Tooltip>
      </span>,
      dataIndex: 'required',
      key: 'required',
      width: 100,
      align: 'center' as const,
      render: (required: boolean, record: CnDatahubCategoryColumn) => (
        <Checkbox
          checked={required}
          disabled={!isFieldEditable('required', record)}
          onChange={(e) => updateColumn(record.id, { required: e.target.checked })}
        />
      ),
    },
    {
      title: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        Affiché
        <Tooltip title="Si la colonne est affichée, l'utilisateur pourra la voir dans l'interface">
          <InfoCircleOutlined />
        </Tooltip>
      </span>,
      dataIndex: 'visible',
      key: 'visible',
      width: 100,
      align: 'center' as const,
      render: (visible: boolean, record: CnDatahubCategoryColumn) => (
        <Checkbox
          checked={visible}
          disabled={!isFieldEditable('visible', record)}
          onChange={(e) => updateColumn(record.id, { visible: e.target.checked })}
        />
      ),
    },
    {
      title: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        Description
        <Tooltip title="Description optionnelle affichée dans l'interface pour décrire la colonne. Pour voir la description, passez la souris sur la colonne.">
          <InfoCircleOutlined />
        </Tooltip>
      </span>,
      dataIndex: 'description',
      key: 'description',
      width: 160,
      render: (_: string | null | undefined, record: CnDatahubCategoryColumn) => (
        <Tooltip title={record.description ?? ''}>
          <Input
            value={record.description ?? ''}
            disabled={!isFieldEditable('description', record)}
            placeholder="Description"
            onChange={(e) => updateColumn(record.id, { description: e.target.value || null })}
          />
        </Tooltip>
      ),
    },
    // 'Personnaliser' button to override the column
    ...(isShared
      ? [
        {
          title: 'Personnaliser',
          key: 'override',
          width: 110,
          align: 'center' as const,
          render: (_: unknown, record: CnDatahubCategoryColumn) => (
            <Checkbox
              checked={isColumnOverridden?.(record) ?? false}
              onChange={(e) => onToggleOverride?.(record, e.target.checked)}
            />
          ),
        },
      ]
      : []),
    // 'Supprimer' button to delete the column (only for private columns)
    ...(!isShared
      ? [
        {
          title: '',
          key: 'actions',
          width: 50,
          render: (_: unknown, record: CnDatahubCategoryColumn) =>
            isPrivateTitleColumn(record) ? (
              <Tooltip title="Colonne obligatoire (identifiant de l'outil)">
                <Button type="text" danger icon={<DeleteOutlined />} disabled />
              </Tooltip>
            ) : (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteColumn(record.id)}
              />
            ),
        },
      ]
      : []),
  ]

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {titleColumn && (
        <Alert
          type="info"
          showIcon
          description={
            <>
              La colonne « {titleDisplayLabel} » sert d&apos;identifiant sur l&apos;outil : libellé
              affiché au survol en vue carte, et lien dans le tableau pour ouvrir la popup de la
              fiche. Elle ne peut pas être supprimée ni être désaffichée.
            </>
          }
        />
      )}
      {/* Table : Liste des colonnes */}
      <Table
        dataSource={sortedColumns}
        columns={tableColumns}
        rowKey={isShared ? columnRowKey : 'id'}
        size="small"
        pagination={false}
        scroll={{ x: 1200 }}
        components={
          {
            body: {
              wrapper: DraggableContainer,
              row: DraggableBodyRow,
            },
          }
        }
      />
      {/* Button to add a new column */}
      {!isShared && (
        <Button type="dashed" icon={<PlusOutlined />} onClick={addColumn}>
          Ajouter une colonne
        </Button>
      )}
      {/* Style for the dragging of the columns */}
      <style>
        {`
          .datahub-column-row-dragging {
            background: #fafafa;
            border: 1px solid #d9d9d9;
            z-index: 9999;
          }
          .datahub-column-row-dragging td {
            padding: 8px;
          }
        `}
      </style>
    </Space>
  )
}
